import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useUsersTableUrlState = ({ sortableKeys, filterKeys, defaultSort }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortKeyRaw = searchParams.get('sortKey') ?? defaultSort.key;
  const sortKeyFromUrl = sortableKeys.includes(sortKeyRaw) ? sortKeyRaw : defaultSort.key;
  const sortDirRaw = searchParams.get('sortDir');
  const sortDirFromUrl =
    sortDirRaw === 'asc' || sortDirRaw === 'desc' ? sortDirRaw : defaultSort.direction;

  const filtersFromUrl = filterKeys.reduce((acc, key) => {
    acc[key] = searchParams.get(key) ?? '';
    return acc;
  }, {});

  const [filters, setFilters] = useState(filtersFromUrl);
  const [sortConfig, setSortConfig] = useState({ key: sortKeyFromUrl, direction: sortDirFromUrl });

  // Serialize filterKeys so that an unstable array reference from the caller
  // does not retrigger this effect on every render (which would flood the
  // browser with navigations and get throttled by Chrome).
  const filterKeysSignature = filterKeys.join(',');

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    filterKeys.forEach((key) => {
      if (filters[key]) next.set(key, filters[key]);
      else next.delete(key);
    });

    next.set('sortKey', sortConfig.key);
    next.set('sortDir', sortConfig.direction);

    if (next.toString() === searchParams.toString()) return;

    setSearchParams(next, { replace: true });
    // filterKeys is intentionally covered via filterKeysSignature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortConfig, searchParams, setSearchParams, filterKeysSignature]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return {
    filters,
    setFilter,
    setFilters,
    sortConfig,
    setSortConfig,
  };
};
